import React, { useEffect, useState } from 'react';
import { apiGet } from '../api';
import '../components/Gallery.css';

export default function GalleryPage({ language = 'en' }) {
  const [sets, setSets] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalItems, setModalItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError('');
      let loadedSets = [];
      try {
        const groups = await apiGet('/api/gallery-sets', { locale: language });
        loadedSets = Array.isArray(groups) ? groups.filter((g) => Array.isArray(g.images) && g.images.length > 0) : [];
        if (!ignore) setSets(loadedSets);
      } catch (_) {}
      try {
        const data = await apiGet('/api/gallery-images', { locale: language, sort: 'order:asc' });
        if (!ignore && Array.isArray(data)) setItems(data);
      } catch (e) {
        if (!ignore) setError('Failed to load gallery');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [language]);

  const getYouTubeThumbnail = (url) => {
    const videoId = url && url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return (videoId && videoId[1]) ? `https://img.youtube.com/vi/${videoId[1]}/maxresdefault.jpg` : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url && url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return (videoId && videoId[1]) ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  const openModal = (list, index) => {
    const safeIndex = Math.max(0, Math.min(index || 0, (list || []).length - 1));
    setModalItems(list || []);
    setCurrentIndex(safeIndex);
    setSelectedItem((list || [])[safeIndex] || null);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setModalItems([]);
    setCurrentIndex(0);
  };

  const showAt = (idx) => {
    if (!modalItems.length) return;
    const n = (idx + modalItems.length) % modalItems.length;
    setCurrentIndex(n);
    setSelectedItem(modalItems[n]);
  };
  const nextItem = () => showAt(currentIndex + 1);
  const prevItem = () => showAt(currentIndex - 1);

  // Keyboard navigation and esc to close
  useEffect(() => {
    if (!selectedItem) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') nextItem();
      if (e.key === 'ArrowLeft') prevItem();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedItem, currentIndex, modalItems.length]);

  // Prevent background scroll when modal open
  useEffect(() => {
    const original = document.body.style.overflow;
    if (selectedItem) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original || ''; };
  }, [selectedItem]);

  const open = (url) => window.open(url, '_blank');

  return (
    <section className="section" style={{ paddingTop: 24 }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{language === 'en' ? 'Gallery' : 'ഗാലറി'}</h2>
          <a href="#" className="btn btn-secondary">{language === 'en' ? 'Back to Home' : 'ഹോം'}</a>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>
            {language === 'en' ? 'Loading...' : 'ലോഡിംഗ്...'}
          </div>
        )}
        {error && (
          <div style={{ textAlign: 'center', color: '#b91c1c', padding: '1rem 0' }}>{error}</div>
        )}

        {/* Grouped view: one thumbnail per group */}
        {(!loading && !error && sets.length > 0) && (
          <div className="card" style={{ padding: 16 }}>
            <div className="gallery-grid">
              {sets.map((group) => {
                const sorted = (group.images || []).slice().sort((a, b) => (a?.order || 0) - (b?.order || 0));
                const first = sorted[0];
                if (!first) return null;
                const type = first.type || (first.youtubeUrl ? 'youtube' : (first.videoUrl ? 'video' : 'image'));
                const thumb = type === 'youtube' ? (getYouTubeThumbnail(first.youtubeUrl) || first.imageUrl) : first.imageUrl;
                const items = sorted.map((g) => {
                  const t = g.type || (g.youtubeUrl ? 'youtube' : (g.videoUrl ? 'video' : 'image'));
                  return {
                    type: t,
                    src: g.imageUrl || null,
                    videoUrl: g.videoUrl || null,
                    youtubeUrl: g.youtubeUrl || null,
                    caption: g.caption || '',
                    description: g.description || '',
                  };
                });
                const caption = group.title || first.caption || (language === 'en' ? 'Gallery Set' : 'ഗാലറി സെറ്റ്');
                const description = first.description || '';
                return (
                  <div key={group.id} className="gallery-item" onClick={() => openModal(items, 0)}>
                    {type === 'youtube' ? (
                      <img src={thumb} alt={caption} />
                    ) : type === 'video' ? (
                      <div className="video-placeholder">{language === 'en' ? 'Video' : 'വീഡിയോ'}</div>
                    ) : (
                      <img src={thumb} alt={caption} />
                    )}
                    <div className="gallery-overlay">
                      <div className="gallery-caption">{caption}</div>
                      <div className="gallery-description">
                        {description?.split('\n').map((line, i) => (
                          <p key={i} className="description-line">{line}</p>
                        ))}
                      </div>
                    </div>
                    {(group.images?.length || 0) > 1 && (
                      <div style={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
                        {(group.images?.length || 0)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* If sets exist, also show ungrouped singles below */}
        {(!loading && !error && sets.length > 0) && (() => {
          const idsInSets = new Set((sets || []).flatMap(g => (g.images || []).map(im => im.id)).filter(Boolean));
          const singles = (items || []).filter((it) => !idsInSets.has(it.id) && !it.groupId);
          if (!singles.length) return null;
          return (
            <div className="card" style={{ padding: 16, marginTop: 16 }}>
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>{language === 'en' ? 'More Photos & Videos' : 'കൂടുതൽ ഫോട്ടോകൾ & വീഡിയോകൾ'}</h3>
              <div className="gallery-grid">
                {singles.map((it, idx) => {
                  const type = it.type || (it.youtubeUrl ? 'youtube' : (it.videoUrl ? 'video' : 'image'));
                  const thumb = type === 'youtube' ? (getYouTubeThumbnail(it.youtubeUrl) || it.imageUrl) : it.imageUrl;
                  return (
                    <div key={it.id || idx} className="gallery-item" onClick={() => open(it.imageUrl || it.videoUrl || it.youtubeUrl)}>
                      {type === 'youtube' ? (
                        <img src={thumb} alt={it.caption || ''} />
                      ) : type === 'video' ? (
                        <div className="video-placeholder">{language === 'en' ? 'Video' : 'വീഡിയോ'}</div>
                      ) : (
                        <img src={thumb} alt={it.caption || ''} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Flat view */}
        {(!loading && !error && sets.length === 0) && (
          <div className="card" style={{ padding: 16 }}>
            <div className="gallery-grid">
              {(() => {
                const idsInSets = new Set((sets || []).flatMap(g => (g.images || []).map(im => im.id)).filter(Boolean));
                return items.filter((it) => !idsInSets.has(it.id) && !it.groupId).map((it, idx) => {
                  const type = it.type || (it.youtubeUrl ? 'youtube' : (it.videoUrl ? 'video' : 'image'));
                  const thumb = type === 'youtube' ? (getYouTubeThumbnail(it.youtubeUrl) || it.imageUrl) : it.imageUrl;
                  return (
                    <div key={it.id || idx} className="gallery-item" onClick={() => open(it.imageUrl || it.videoUrl || it.youtubeUrl)}>
                      {type === 'youtube' ? (
                        <img src={thumb} alt={it.caption || ''} />
                      ) : type === 'video' ? (
                        <div className="video-placeholder">{language === 'en' ? 'Video' : 'വീഡിയോ'}</div>
                      ) : (
                        <img src={thumb} alt={it.caption || ''} />
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {(!loading && !error && sets.length === 0 && items.length === 0) && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>
            {language === 'en' ? 'No gallery items found.' : 'ഗാലറി ഇനങ്ങൾ ലഭ്യമല്ല.'}
          </div>
        )}

        {/* Modal Popup */}
        {selectedItem && (
          <div className="gallery-modal" onClick={closeModal}>
            <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="gallery-modal-close" onClick={closeModal}>&times;</button>

              <div className="gallery-modal-row">
                <button className="gallery-nav-btn" onClick={prevItem} aria-label="Previous">&#10094;</button>
                <div className="gallery-modal-media" style={{ flex: 1 }}>
                  {selectedItem.type === 'youtube' && selectedItem.youtubeUrl ? (
                    <iframe
                      src={getYouTubeEmbedUrl(selectedItem.youtubeUrl)}
                      title={selectedItem.caption || ''}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : selectedItem.type === 'video' && (selectedItem.videoUrl || selectedItem.src) ? (
                    <video controls autoPlay>
                      <source src={selectedItem.videoUrl || selectedItem.src} />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <img src={selectedItem.src} alt={selectedItem.caption || ''} />
                  )}
                </div>
                <button className="gallery-nav-btn" onClick={nextItem} aria-label="Next">&#10095;</button>
              </div>

              <div className="gallery-modal-details">
                <h3 className="gallery-modal-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{selectedItem.caption || ''}</span>
                  {modalItems.length > 0 && <span style={{ fontSize: 14, color: '#666' }}>{currentIndex + 1} / {modalItems.length}</span>}
                </h3>
                <div className="gallery-modal-description">
                  {(selectedItem.description || '').split('\n').map((line, lineIndex) => (
                    line ? <p key={lineIndex}>{line}</p> : null
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
